import { css } from '@emotion/react';
import {
  type FC, useCallback, useEffect, useRef,
} from 'react';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { RangeType } from '~/_shared/utils/range/range.helpers';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import type { BoundaryTerritoryId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  type BoundaryTerritory, type BoundaryTerritoryGroup, type BoundaryTerritoryGroupStyle,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import {
  BOUNDARY_MAX_VALUE_ID, BOUNDARY_MIN_VALUE_ID, bucketTypeToRangeType,
} from '../../../../../boundary/settings/boundarySettings.helpers';
import {
  recalculateBoundaryTerritoriesValues, validateBoundarySettingsNumericValue,
} from '../../../../../boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { useAdjustedBoundaryRangeMinMax } from '../../../../../boundary/settings/numeric/useAdjustedBoundaryRangeMinMax.hook';
import { BoundaryFillSettingsNumericalGeneralComponent } from './boundaryFillSettingsNumericalGeneral.component';
import { BoundaryFillSettingsNumericTerritoriesTableComponent } from './boundaryFillSettingsNumericTerritoriesTable.component';

const containerStyle = css({
  width: '100%',
});

type BoundarySettingsNumericWithoutCommonProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  valueIsInitial: boolean;
  disableRangeTypeChange: boolean;
  isValid: boolean;
  isDecimal: boolean;
  max: number | typeof BOUNDARY_MAX_VALUE_ID;
  min: number | typeof BOUNDARY_MIN_VALUE_ID;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  onNumberOfRangesChange: (numberOfRanges: number, style: BoundaryTerritoryGroupStyle, rangeType: RangeType) => void;
  onRecalculateRangesClick: () => void;
  setIsFormValid: (isFormValid: boolean) => void;
}>;

export const BoundaryFillSettingsNumericComponent: FC<BoundarySettingsNumericWithoutCommonProps> = (props) => {
  const { boundaryTerritoryGroup, onBoundaryTerritoryGroupChange } = props;
  const setIsFormValidRef = useRef(props.setIsFormValid);
  const boundaryTerritories = boundaryTerritoryGroup.settings.boundaryTerritories;

  const rangeType = bucketTypeToRangeType(boundaryTerritoryGroup.settings.bucketType) ?? RangeType.Value;
  const prevRangeType = usePrevious(rangeType);

  const { rangeMin, rangeMax } = useAdjustedBoundaryRangeMinMax(props.min, props.max, boundaryTerritories, rangeType);

  const prevRangeMin = usePrevious(rangeMin);
  const prevRangeMax = usePrevious(rangeMax);

  const isDecimalMode = props.isDecimal && rangeType === RangeType.Value;
  const dataTableValueSuffix = rangeType === RangeType.Percentage ? '%' : '';

  const handleChangeTerritoryValue = useCallback((territories: ReadonlyArray<BoundaryTerritory>) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: territories,
      },
    });
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const handleChangeBoundaryTerritoryName = (id: BoundaryTerritoryId, name: string) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: boundaryTerritoryGroup.settings.boundaryTerritories.map(
          bt => bt.boundaryTerritoryId === id ?
            {
              ...bt,
              displayName: name,
              translate: false,
            } : bt,
        ),
      },
    });
  };

  // validate boundary territories, notify parent on validation results
  useEffect(() => {
    for (let i = 0; i < boundaryTerritories.length; i++) {
      const bt = boundaryTerritories[i];
      if (!bt) {
        return;
      }

      if (!validateBoundarySettingsNumericValue(
        bt,
        boundaryTerritories[i - 1] ?? null,
        boundaryTerritories[i + 1] ?? null,
      )) {
        setIsFormValidRef.current(false);
        return;
      }

      if (isTextEmpty(bt.displayName)) {
        setIsFormValidRef.current(false);
        return;
      }

      setIsFormValidRef.current(true);
    }
  }, [boundaryTerritories]);

  // recalculate boundary territories values on range type change
  useEffect(() => {
    if (props.valueIsInitial) {
      return;
    }

    if (prevRangeMin === undefined || prevRangeMax === undefined) {
      return;
    }

    if (prevRangeMin === BOUNDARY_MIN_VALUE_ID || prevRangeMax === BOUNDARY_MAX_VALUE_ID) {
      return;
    }

    if (props.min === BOUNDARY_MIN_VALUE_ID || props.max === BOUNDARY_MAX_VALUE_ID) {
      return;
    }

    if (rangeType === prevRangeType) {
      return;
    }

    if (rangeType === RangeType.Value) {
      // was change from RangeType.Percentage to RangeType.Value so prev min was 0 and prev max was 100
      handleChangeTerritoryValue(recalculateBoundaryTerritoriesValues(boundaryTerritories,
        props.min,
        props.max,
        0,
        100,
        isDecimalMode));
    }
    else {
      handleChangeTerritoryValue(recalculateBoundaryTerritoriesValues(boundaryTerritories,
        0,
        100,
        prevRangeMin,
        prevRangeMax,
        isDecimalMode));
    }
  },
  [rangeMin, rangeMax, prevRangeMin, prevRangeMax, rangeType, isDecimalMode, handleChangeTerritoryValue,
    boundaryTerritories,
    prevRangeType, props.min, props.max, props.valueIsInitial]);

  return (
    <div css={containerStyle}>
      <BoundaryFillSettingsNumericalGeneralComponent
        {...props}
        rangeMax={rangeMax}
        rangeMin={rangeMin}
        rangeType={rangeType}
      />

      <BoundaryFillSettingsNumericTerritoriesTableComponent
        boundaryTerritoryGroup={boundaryTerritoryGroup}
        rangeMin={rangeMin}
        rangeMax={rangeMax}
        dataTableValueSuffix={dataTableValueSuffix}
        isDecimalMode={isDecimalMode}
        onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
        onBoundaryTerritoryChange={handleChangeTerritoryValue}
        onTerritoryNameChange={handleChangeBoundaryTerritoryName}
        onRecalculateRangesClick={props.onRecalculateRangesClick}
      />
    </div>
  );
};
